import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Backdrop, Card, CardContent, CircularProgress } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { Amplify } from 'aws-amplify'
import { useFeatures } from 'flagged'
import { useTheme } from '../../../../components/mui/Styles/index'
import { Box, Button, TextField } from '../../../../components/mui/Core/index'
import FormHeading from '../../../../components/userauthentication/FormHeading'
import awsconfig from '../../../../aws-exports'
import { completeNewPassword } from '../../../../api/userauthentication/signup'
import GenericSnackbar from '../../../../components/GenericSnackbar/generic-snackbar'
import { businessMessages } from '../../../../constants/business-message-constants'
import { changeFirstPassword } from '../../../../api/user/change-first-password'
import Constants from '../../../../constants/en.json'
import useBrowserTabTitles from '../../../../hooks/browserTabTitles/browser-tab-titles'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'
import PasswordValidation from '../../../../components/PasswordValidation/password-validation'
import { routePaths } from '../../../../constants/routes'

Amplify.configure(awsconfig)

function ProfileSetup() {
  const theme = useTheme()
  const dispatch = useDispatch()
  useBrowserTabTitles(Constants.profileSetup.SETUP_YOUR_PROFILE)
  const navigate = useNavigate()
  const features = useFeatures()
  const location = useLocation()
  const isUserServiceEnabled = features.isUserServiceEnabled
  const userSignUpInfo = location.state
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const isFormValid =
    (isUserServiceEnabled && !firstName) ||
    (isUserServiceEnabled && !lastName) ||
    !newPassword ||
    !confirmPassword ||
    !isPasswordValid ||
    newPassword !== confirmPassword

  const formHeading = isUserServiceEnabled
    ? Constants.profileSetup.SETUP_YOUR_PROFILE
    : Constants.profileSetup.CHANGE_YOUR_PASSWORD

  const navigateToLogin = () => {
    navigate(routePaths.LOGIN)
  }

  function handleProfileSetupSuccess(response) {
    setIsLoading(false)
    if (!isUserServiceEnabled) {
      /* Display Success message when password set successfully */
      dispatch(
        setSnackbar({
          message: businessMessages.password.RESET_SUCCESS_MESSAGE,
          severity: 'success',
          action: true,
          autoHideDuration: 3000,
        }),
      )
      /* Allow 3 seconds to elapse so that user can see success message
           before being redirected to the login page */
      setTimeout(() => {
        navigateToLogin()
      }, 3500)
    }
    if (isUserServiceEnabled) {
      const mfaData = {
        sessionId: response.data.data?.sessionId,
        qrCodeUrl: response.data.data?.qrCodeUrl,
      }
      navigate('/-signup/enable-mfa', { state: mfaData })
    }
  }

  function handleProfileSetupError(exception) {
    setIsLoading(false)
    dispatch(
      setSnackbar({
        message: exception?.message || businessMessages.password.RESET_FAILURE_MESSAGE,
        severity: 'error',
        action: true,
      }),
    )
  }

  const handleProfileSetup = () => {
    setIsLoading(true)
    if (!isUserServiceEnabled) {
      completeNewPassword(userSignUpInfo, newPassword)
        .then(() => handleProfileSetupSuccess())
        .catch(() => handleProfileSetupError())
    }

    if (isUserServiceEnabled) {
      const request = {
        challengeType: userSignUpInfo.challengeType,
        challengeName: userSignUpInfo.challengeName,
        firstName,
        lastName,
        sessionId: userSignUpInfo.confirmedUserSessionId,
        username: userSignUpInfo.username,
        password: newPassword,
        passwordConfirm: confirmPassword,
      }
      changeFirstPassword(request)
        .then((response) => handleProfileSetupSuccess(response))
        .catch((exception) => handleProfileSetupError(exception))
    }
  }

  const handleOnFirstNameChange = (event) => {
    setFirstName(event.target.value)
  }

  const handleOnLastNameChange = (event) => {
    setLastName(event.target.value)
  }

  const updateTextInput = (event, field) => {
    if (field === 'validate') {
      setNewPassword(event.target.value)
    } else if (field === 'compare') {
      setConfirmPassword(event.target.value)
    }
  }

  const handlePasswordValidation = (isValid) => {
    setIsPasswordValid(isValid)
  }

  const cardContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }
  const passwordRulesContainerStyle = { my: 3, width: 240 }

  return (
    <Box sx={theme.soloContainer}>
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Card className='soloCard'>
        <CardContent sx={cardContentStyle}>
          <FormHeading formHeader={formHeading} />
          {isUserServiceEnabled && (
            <>
              <TextField
                id='profile-setup-first-name-field'
                value={firstName}
                onChange={handleOnFirstNameChange}
                required
                label={Constants.FIRST_NAME}
                variant='outlined'
                type='text'
                sx={{ width: '240px' }}
              />
              <TextField
                id='profile-setup-last-name-field'
                value={lastName}
                onChange={handleOnLastNameChange}
                required
                label={Constants.LAST_NAME}
                variant='outlined'
                type='text'
                sx={{ width: '240px' }}
              />
            </>
          )}
          <TextField
            id='profile-setup-new-password-field'
            onChange={(e) => {
              updateTextInput(e, 'validate')
            }}
            required
            label={Constants.profileSetup.CREATE_NEW_PASSWORD}
            variant='outlined'
            type='password'
            placeholder={Constants.profileSetup.CREATE_NEW_PASSWORD}
            value={newPassword}
            sx={{ width: '240px' }}
            autoComplete='new-password'
          />
          <TextField
            id='profile-setup-confirm-new-password-field'
            onChange={(e) => {
              updateTextInput(e, 'compare')
            }}
            required
            label={Constants.profileSetup.CONFIRM_NEW_PASSWORD}
            variant='outlined'
            type='password'
            placeholder={Constants.profileSetup.CONFIRM_NEW_PASSWORD}
            value={confirmPassword}
            sx={{ width: '240px' }}
            autoComplete='new-password'
          />
          <Box sx={passwordRulesContainerStyle}>
            <PasswordValidation
              value={newPassword}
              handlePasswordValidation={handlePasswordValidation}
            />
          </Box>
          <Button
            variant='contained'
            sx={{ mt: 2 }}
            disabled={isFormValid || isLoading}
            onClick={handleProfileSetup}
            color='secondary'
          >
            {isUserServiceEnabled
              ? Constants.profileSetup.COMPLETE_PROFILE
              : Constants.profileSetup.CHANGE_PASSWORD}
          </Button>
        </CardContent>
      </Card>
      <GenericSnackbar />
    </Box>
  )
}

export default ProfileSetup
